/**
 * Owns what the client holds because a trip is current: the socket room that
 * delivers the trip's broadcasts, and the itinerary and saved-places caches
 * that many selectors read without subscribing to them.
 *
 * Their lifetime is the trip's, not any component's: holding them in page
 * effects re-subscribed on every render, and broadcasts arriving between the
 * leave and the rejoin were lost.
 */

import type {
  CollaboratorEventData,
  TripEvent,
  TripEventData,
} from "@/types/socket"
import type { TripRef } from "@/types/trip"
import type { UserRef } from "@/types/user"

import { isServerSide } from "@/lib/is-server-side"
import { ObjectSubscriptionManager } from "@/lib/object-subscription-manager"
import { itineraryApi } from "@/lib/storage/api/itinerary-api"
import { savedPlacesApi } from "@/lib/storage/api/saved-places-api"
import {
  receiveCollaboratorEvent,
  receiveTripEvent,
} from "@/lib/storage/collaboration-thunks"
import { createMtListenerMiddleware } from "@/lib/storage/create-mt-listener-middleware"
import type { AppDispatch, RootState } from "@/lib/storage/redux-store"
import { selectTrip } from "@/lib/storage/trip-slice"

const tripSubscriptionMiddleware = createMtListenerMiddleware(
  "trip-subscription-middleware"
)
const startAppListening = tripSubscriptionMiddleware.startListening.withTypes<
  RootState,
  AppDispatch
>()

interface Room {
  tripId: number
  onTripEvent: (data: TripEventData) => void
}

let room: Room | undefined

function selectRoomTripId(state: RootState): number | undefined {
  // An anonymous user is never authorized for a trip room. Read the user
  // directly rather than through selectUserIsAnonymous, which asserts one is
  // present: this runs on every action, including before bootstrap.
  const user = state.userSlice.user
  if (!user || user.provider === "anonymous") return undefined
  return selectTrip(state)?.id
}

function joinRoom(tripId: number, dispatch: AppDispatch): Room {
  const onTripEvent = (data: TripEventData) => {
    void dispatch(receiveTripEvent({ data }))
  }
  const onCollaboratorEvent = (
    userRef: UserRef,
    data: CollaboratorEventData
  ) => {
    void dispatch(receiveCollaboratorEvent({ userRef, data }))
  }
  void ObjectSubscriptionManager.instance.subscribeToTrip(
    tripId,
    onTripEvent,
    onCollaboratorEvent
  )
  return { tripId, onTripEvent }
}

function leaveRoom(leaving: Room) {
  ObjectSubscriptionManager.instance.unsubscribe<TripEvent>(leaving.onTripEvent)
}

interface Caches {
  tripRef: TripRef
  subscriptions: Array<{ unsubscribe: () => void }>
}

let caches: Caches | undefined

function selectCachedTripRef(state: RootState): TripRef | undefined {
  // Only a user-created trip has an itinerary and saved places to hold.
  const trip = selectTrip(state)
  return trip?.user_created ? trip.ref : undefined
}

function holdCaches(holding: Caches, dispatch: AppDispatch) {
  const { tripRef } = holding
  holding.subscriptions.push(
    dispatch(
      itineraryApi.endpoints.getItinerary.initiate(
        { tripRef },
        { forceRefetch: true }
      )
    ),
    dispatch(savedPlacesApi.endpoints.getSavedPlacesByTrip.initiate(tripRef))
  )
}

function releaseCaches(releasing: Caches) {
  releasing.subscriptions.forEach((subscription) => subscription.unsubscribe())
}

// Each listener keys on one selector and holds one record. The record is
// updated before anything is dispatched, so the actions a change produces
// find the listener already settled. Comparing state against the record,
// rather than matching actions or diffing the previous state, is what lets a
// store that already holds a trip reconcile on the next action of any kind.
if (!isServerSide()) {
  startAppListening({
    predicate: (_action, state) => selectRoomTripId(state) !== room?.tripId,
    effect: (_action, { dispatch, getState }) => {
      const leaving = room
      room = undefined
      // Leave before joining: the manager keeps one collaborator callback,
      // and leaving a trip clears it.
      if (leaving) leaveRoom(leaving)
      const tripId = selectRoomTripId(getState())
      if (tripId) room = joinRoom(tripId, dispatch)
    },
  })

  startAppListening({
    predicate: (_action, state) =>
      selectCachedTripRef(state) !== caches?.tripRef,
    effect: (_action, { dispatch, getState }) => {
      const releasing = caches
      const tripRef = selectCachedTripRef(getState())
      caches = tripRef ? { tripRef, subscriptions: [] } : undefined
      if (caches) holdCaches(caches, dispatch)
      if (releasing) releaseCaches(releasing)
    },
  })
}

export function __resetTripSubscriptionForTesting() {
  if (room) leaveRoom(room)
  if (caches) releaseCaches(caches)
  room = undefined
  caches = undefined
}

export { tripSubscriptionMiddleware }
